import React, { useEffect, useState } from 'react';
import { Descriptions, Radio, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import Button from '../../../components/Button';
import {
  useDeleteCopyBookMutation,
  useGetCopyBookInformationQuery,
} from '../../../api/books/bookManagement.api';
import CopyBookRegisterModal from '../../../components/book/modals/CopyBookRegisterModal';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import { useSnackbar } from 'notistack';
import { successVariant, warningVariant } from '../../../lib/barVariant';

interface CopyBasicInformationType {
  bookNo: number;
  bookSeq: number | null;
  campusCode: string;
}

const BookCopyBasicInformation = ({
  bookNo,
  bookSeq,
  campusCode,
}: CopyBasicInformationType) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const [isVisible, setIsVisible] = useState(false);
  const [isDeleteVisible, setIsDeleteVisible] = useState(false);
  const genres = commonCodes.find(
    (item) => item.commonCode === 'BK0001',
  )?.detailCode;
  const [deleteCopyBookTrigger, { isSuccess }] = useDeleteCopyBookMutation();
  const { data: response } = useGetCopyBookInformationQuery({
    bookNo,
    bookSeq,
    campusCode,
  });

  let information = response?.data;

  const handleCopyBookDelete = () => {
    if (!information?.clientBookNo) {
      enqueueSnackbar(
        t('booksManagement.books.message21'),
        warningVariant,
      );
      return;
    }

    deleteCopyBookTrigger({
      clientBookNo: information?.clientBookNo,
      lendingCode: information?.lendingCode,
    });
  };

  useEffect(() => {
    if (!isSuccess) return;

    enqueueSnackbar(t('booksManagement.books.message22'), successVariant);
    setIsDeleteVisible(false);
    information = {};
  }, [isSuccess]);

  return (
    <>
      <div className='comm-table__area rowType'>
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item
            label={`${t('booksManagement.books.column2')} *`}
          >
            {information?.bookName}
          </Descriptions.Item>
          <Descriptions.Item
            label={`${t('booksManagement.books.column3')} *`}
          >
            {information?.author}
          </Descriptions.Item>
          <Descriptions.Item
            label={`${t('booksManagement.books.column4')} *`}
          >
            {information?.isbn}
          </Descriptions.Item>
          <Descriptions.Item
            label={`${t('booksManagement.books.column5')} *`}
          >
            {information?.publisherName}
          </Descriptions.Item>
          <Descriptions.Item label={t('booksManagement.books.column6')}>
            {information?.publicationYear ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={`${t('booksManagement.books.column43')}`}>
            {information?.copyCount ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={`${t('booksManagement.books.column19')}`}>
            {information?.bookSeq ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('booksManagement.books.column7')}>
            {information?.seriesName}
          </Descriptions.Item>
          <Descriptions.Item label={t('booksManagement.books.column8')}>
            <Select
              className="w-100"
              placeholder={t('booksManagement.books.column8')}
              value={information?.genreDiv}
              disabled={true}
            >
              {genres?.map((item) => (
                <Select.Option key={item.detailCode} value={item.detailCode}>
                  {item.korName}
                </Select.Option>
              ))}
            </Select>
          </Descriptions.Item>
          <Descriptions.Item
            label={`${t('booksManagement.books.column9')} *`}
          >
            <div className='auth-radio__area'>
              <Radio.Group
                name="bookDiv"
                value={information?.bookDiv}
                disabled={true}
              >
                <Radio value={'FC'}>
                  {t('booksManagement.books.column44')}
                </Radio>
                <Radio className="ml-3" value={'NF'}>
                  {t('booksManagement.books.column45')}
                </Radio>
              </Radio.Group>
            </div>
          </Descriptions.Item>
          <Descriptions.Item
            label={t('booksManagement.books.column10')}
          >
            {information?.theme ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={`${t('booksManagement.books.column11')} *`}
          >
            {information?.courseName}
          </Descriptions.Item>
          <Descriptions.Item label={t('booksManagement.books.column12')}>
            {information?.bookLevel ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('booksManagement.books.column47')}>
            {information?.specialDesc}
          </Descriptions.Item>
        </Descriptions>
      </div>
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
            {information?.clientBookNo && (
              <Button
                className="btn-basic gray"
                onClick={() => setIsDeleteVisible(true)}
                text={t('booksManagement.books.button6')}
              />
            )}
            <Button
              className="btn-basic pink"
              onClick={() => setIsVisible(true)}
              text={t('booksManagement.books.button7')}
            />
        </div>
      </div>
      {isVisible && (
        <CopyBookRegisterModal
          visible={isVisible}
          confirmText={t('common.button.create')}
          cancelText={t('common.button.cancel')}
          titleText={t('booksManagement.books.button7')}
          bookNo={bookNo}
          campusCode={campusCode}
          onCancel={() => setIsVisible(false)}
        />
      )}

      {
        <ConfirmModal
          visible={isDeleteVisible}
          setVisible={setIsDeleteVisible}
          onConfirm={handleCopyBookDelete}
          titleText={t('booksManagement.books.title2')}
          messageText={t('booksManagement.books.message23')}
          cancelText={t('common.button.cancel')}
          confirmText={t('common.button.confirm')}
        />
      }
    </>
  );
};

export default BookCopyBasicInformation;
